import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  addUser,
  checkUserExists,
  getPracticeProviderLicenses,
} from "apiHandler/actions";
import React, { useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import "./styles.css";

const roleOptions = ["Admin", "Staff", "Provider", "Coder", "Scribe"];
const statusOptions = ["Active", "Inactive"];
const accessOptions = [
  { label: "General", value: "general" },
  { label: "CSR", value: "csr" },
  { label: "Scribing", value: "scribing" },
  { label: "Care Quality", value: "care_quality" },
];
const passwordRequirements = [
  { key: "minLength", label: "At least 8 characters" },
  { key: "hasNumber", label: "At least 1 number" },
  {
    key: "hasSpecialChar",
    label: "At least 1 special character",
  },
  { key: "hasUppercase", label: "At least 1 uppercase letter" },
  { key: "hasLowercase", label: "At least 1 lowercase letter" },
];

const UsersAction = ({
  form,
  handleClose,
  open,
  fetchUsers,
  setForm,
  userData,
}) => {
  const [emailError, setEmailError] = useState("");

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success", // or 'error'
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailBlur = async () => {
    const email = form.user_id;
    if (!validateEmail(email)) {
      setEmailError("Invalid email format");
      return;
    }
    setEmailError("");
    setCheckingEmail(true);
    try {
      const res = await checkUserExists(
        {
          user_id: email,
        },
        userData?.access_token,
      );

      if (res?.status === "error") {
        setEmailError(res.message || "Error checking user");
      } else if (res?.data?.exists) {
        setEmailError("User ID already exists");
      }
    } catch (err) {
      console.error("Error checking user existence", err);
      setEmailError("Something went wrong");
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePracticeChange = (event) => {
    const { value } = event.target;

    setForm((prev) => ({
      ...prev,
      practice_id_list: prev.user_role === "Provider" ? [value] : value,
    }));
  };

  const validatePassword = (password = "") => ({
    minLength: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
  });

  const handleSave = async () => {
    if (emailError || !form.user_id || !validateEmail(form.user_id)) return;
    const passwordValidation = validatePassword(form.password || "");

    const invalidRequirements = passwordRequirements.filter(
      (requirement) => !passwordValidation[requirement.key],
    );

    if (invalidRequirements.length > 0) {
      setSnack({
        open: true,
        message: `Invalid password. Please meet these requirements: ${invalidRequirements
          .map((requirement) => requirement.label)
          .join(", ")}.`,
        severity: "error",
      });

      return;
    }
    if (form.user_role === "Provider") {
      try {
        const res = await getPracticeProviderLicenses(
          { practice_id: userData?.selected_practice },
          userData?.access_token,
        );
        const availableLicenses = res?.practice_provider_licenses;
        const usedLicenses = res?.provider_licenses_used || 0;

        if (availableLicenses !== null && availableLicenses <= usedLicenses) {
          setSnack({
            open: true,
            message:
              "You have reached the maximum number of provider licenses. Please contact Orci Care Sales team to request additional licenses.",
            severity: "error",
          });
          return;
        }
      } catch (error) {
        console.error("Error fetching provider licenses", error);
        setSnack({
          open: true,
          message: "Could not verify provider license availability. Try again.",
          severity: "error",
        });
        return;
      }
    }
    const updatedUser = {
      ...form,
      user_name: `${form.first_name} ${form.last_name}`,
      id: Date.now(),
    };

    try {
      const res = await addUser(updatedUser, userData?.access_token);

      if (res && res?.status === "success") {
        setSnack({
          open: true,
          message: "User added successfully!",
          severity: "success",
        });
        handleClose();
        fetchUsers();
      } else {
        throw new Error(res?.message || "Something went wrong");
      }
    } catch (error) {
      console.log("error: ", error);
      setSnack({
        open: true,
        message: "Failed to save user. Please try again.",
        severity: "error",
      });
    }
  };

  const generateRandomPassword = (length = 12) => {
    if (length < 8) {
      throw new Error("Password length must be at least 8 characters.");
    }

    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = "!@#$%^&*()-_=+";

    const allChars = uppercase + lowercase + numbers + specialChars;

    // Include at least one character from every required category.
    const passwordChars = [
      uppercase[Math.floor(Math.random() * uppercase.length)],
      lowercase[Math.floor(Math.random() * lowercase.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      specialChars[Math.floor(Math.random() * specialChars.length)],
    ];

    while (passwordChars.length < length) {
      passwordChars.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the characters.
    for (let i = passwordChars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordChars[i], passwordChars[j]] = [
        passwordChars[j],
        passwordChars[i],
      ];
    }

    return passwordChars.join("");
  };
  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Add User</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
      >
        <TextField
          label="Email"
          name="user_id"
          value={form.user_id}
          onChange={handleChange}
          onBlur={handleEmailBlur}
          error={!!emailError}
          helperText={emailError}
          fullWidth
          variant="outlined"
          margin="normal"
        />
        <TextField
          label="First Name"
          name="first_name"
          value={form.first_name}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Last Name"
          name="last_name"
          value={form.last_name}
          onChange={handleChange}
          fullWidth
        />

        <FormControl fullWidth>
          <PhoneInput
            international
            defaultCountry="US"
            placeholder="Enter phone number"
            value={form.phone || ""}
            onChange={(value) =>
              setForm((prev) => ({
                ...prev,
                phone: value,
              }))
            }
            className="phone-input"
          />
        </FormControl>

        <Box
          display="flex"
          gap={2}
          alignItems="flex-start"
          sx={{ width: "100%" }}
        >
          <TextField
            label="Password"
            name="password"
            value={form.password || ""}
            onChange={handleChange}
            type="text"
            fullWidth
            error={
              !!form.password &&
              Object.values(validatePassword(form.password)).some(
                (isValid) => !isValid,
              )
            }
            helperText={
              <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                <Box component="span" sx={{ display: "block", mb: 0.5 }}>
                  Password requirements:
                </Box>

                {passwordRequirements.map((requirement) => {
                  const isValid = validatePassword(form.password || "")[
                    requirement.key
                  ];

                  return (
                    <Box
                      key={requirement.key}
                      component="span"
                      sx={{
                        display: "block",
                        color: isValid ? "success.main" : "error.main",
                        fontSize: "0.75rem",
                        lineHeight: 1.8,
                      }}
                    >
                      {isValid ? "✓" : "✗"} {requirement.label}
                    </Box>
                  );
                })}
              </Box>
            }
          />

          <Button
            variant="outlined"
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                password: generateRandomPassword(),
              }))
            }
            sx={{
              height: 56,
              minWidth: 120,
              flexShrink: 0,
              alignSelf: "flex-start",
              mt: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            Generate
          </Button>
        </Box>

        <TextField
          label="Role"
          name="user_role"
          value={form.user_role}
          onChange={handleChange}
          select
          fullWidth
        >
          {roleOptions.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Status"
          name="user_status"
          value={form.user_status}
          onChange={handleChange}
          select
          fullWidth
        >
          {statusOptions.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <FormControl fullWidth>
          <InputLabel>Practice Access</InputLabel>
          <Select
            multiple={form.user_role !== "Provider"}
            value={
              form.user_role === "Provider"
                ? form.practice_id_list?.[0] || ""
                : form.practice_id_list || []
            }
            onChange={handlePracticeChange}
            input={<OutlinedInput label="Practice Access" />}
            renderValue={(selected) => {
              const practices = userData?.practices || [];
              if (form.user_role === "Provider") {
                const practice = practices.find(
                  (p) => p.practice_id === selected,
                );
                return practice ? practice.practice_name : "";
              } else {
                return practices
                  .filter((p) => selected.includes(p.practice_id))
                  .map((p) => p.practice_name)
                  .join(", ");
              }
            }}
          >
            {(userData?.practices || []).map((practice) => (
              <MenuItem key={practice.practice_id} value={practice.practice_id}>
                {form.user_role !== "Provider" && (
                  <Checkbox
                    checked={form.practice_id_list?.includes(
                      practice.practice_id,
                    )}
                  />
                )}
                <ListItemText
                  primary={`${practice.practice_name} (${practice.practice_id})`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel shrink>Access List</InputLabel>
          <Box display="flex" flexDirection="row" gap={2} sx={{ mt: 1, px: 1 }}>
            {accessOptions.map((option) => (
              <Box key={option.value}>
                <Checkbox
                  checked={form.access_list.includes(option.value)}
                  onChange={(e) => {
                    const updatedAccessList = e.target.checked
                      ? [...form.access_list, option.value]
                      : form.access_list.filter(
                          (item) => item !== option.value,
                        );
                    setForm((prev) => ({
                      ...prev,
                      access_list: updatedAccessList,
                    }));
                  }}
                />
                <ListItemText primary={option.label} />
              </Box>
            ))}
          </Box>
        </FormControl>
        {form.user_role === "Provider" && (
          <>
            <TextField
              label="NPI"
              name="npi"
              value={form.npi || ""}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Title"
              name="title"
              value={form.title || ""}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Appointment Based Scribing"
              name="appointment_based_scribing"
              value={form.appointment_based_scribing || ""}
              onChange={handleChange}
              select
              fullWidth
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
            <TextField
              label="Review Required"
              name="review_required"
              value={form.review_required || ""}
              onChange={handleChange}
              select
              fullWidth
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            !!emailError ||
            checkingEmail ||
            !Object.values(validatePassword(form.password || "")).every(Boolean)
          }
        >
          {"Add"}
        </Button>
      </DialogActions>
      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default UsersAction;
